<template>
  <div class="form-container">
    <div class="form">
      <div class="form__title">
        <h1>Create a User</h1>
        <p>Grant user access to the Client Portal</p>
      </div>
      <q-form
        @submit.prevent="onSubmitCreateUser"
        style="width: 100%"
        class="q-gutter-y-md"
      >
        <!-- Email -->
        <q-input
          ref="input"
          outlined
          type="email"
          v-model="form.email"
          label="Email *"
          color="secondary"
          lazy-rules
          :rules="[(val) => !!val || '* Email is missing', isValidEmail]"
        >
          <template v-slot:prepend>
            <q-icon name="mail_outline" />
          </template>
        </q-input>
        <!-- Name -->
        <div class="row q-col-gutter-md q-gutter-y-md">
          <div class="col">
            <q-input
              outlined
              type="text"
              v-model="form.firstname"
              label="First Name *"
              color="secondary"
              lazy-rules
              :rules="[
                (val) => !!val || '* Surname is missing',
                (val) => val.length > 1 || 'Please enter your surname name',
              ]"
            >
              <template v-slot:prepend>
                <q-icon name="mood" />
              </template>
            </q-input>
          </div>

          <div class="col">
            <q-input
              outlined
              type="text"
              v-model="form.surname"
              label="Surname *"
              color="secondary"
              lazy-rules
              :rules="[
                (val) => !!val || '* First name is missing',
                (val) => val.length > 1 || 'Please enter your first name',
              ]"
            >
              <template v-slot:prepend>
                <q-icon name="mood" />
              </template>
            </q-input>
          </div>
        </div>

        <!-- User Roles -->
        <q-select
          v-model="form.userRoles"
          :options="roleList"
          :clearable="form.userRoles !== 0 ? true : false"
          clear-icon="close"
          @clear="form.userRoles = []"
          :behavior="$q.platform.is.ios === true ? 'dialog' : 'menu'"
          option-value="role_id"
          option-label="role_name"
          outlined
          color="secondary"
          multiple
          use-chips
          class="select"
          popup-content-class="select__options"
          options-dense
          label="User Roles *"
          lazy-rules
          :rules="[(val) => (val && val.length > 0) || '* User roles missing']"
        >
          <template v-slot:option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
              class="select__options--item"
            >
              <q-item-section>
                <q-item-label>
                  {{ scope.opt.role_name }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
          <template v-slot:prepend>
            <q-icon name="podcasts" />
          </template>
        </q-select>

        <!-- PBI Roles -->

        <!-- User Roles -->
        <q-select
          v-model="form.pbiRoles"
          :options="pbiRoleList"
          :clearable="form.pbiRoles !== 0 ? true : false"
          clear-icon="close"
          @clear="form.pbiRoles = []"
          :behavior="$q.platform.is.ios === true ? 'dialog' : 'menu'"
          option-value="pbi_role_id"
          option-label="pbi_role_name"
          outlined
          color="secondary"
          multiple
          use-chips
          class="select"
          popup-content-class="select__options"
          options-dense
          label="PBI Roles *"
          lazy-rules
          :rules="[(val) => (val && val.length > 0) || '* PBI roles missing']"
          ><template v-slot:option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
              class="select__options--item"
            >
              <q-item-section>
                <q-item-label>
                  {{ scope.opt.pbi_role_name }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
          <template v-slot:prepend>
            <q-icon name="podcasts" />
          </template>
        </q-select>

        <!-- Show all Operators & Networks checkbox -->
        <div class="row q-col-gutter-md">
          <q-checkbox
            v-if="!isClientAdminUser()"
            v-model="form.seeAllOperatorsAndNetworks"
            label="Give user access to all Operators & Networks?"
            color="secondary"
            @input="alert(form.seeAllOperatorsAndNetworks)"
          />
          <q-dialog v-model="confirm" persistent>
            <q-card>
              <q-card-section>
                <div class="text-h6">Warning</div>
              </q-card-section>

              <q-item class="q-pt-none">
                <q-item-section avatar>
                  <q-icon name="warning" class="text-red" />
                </q-item-section>

                <q-item-section>
                  <q-item-label
                    >Are you sure you want to grant this user with access to all
                    Operator & Network data? By clicking <strong>Yes</strong>,
                    the Operators and Networks inputs will be cleared and
                    disabled.</q-item-label
                  >
                </q-item-section>
              </q-item>

              <q-card-actions align="right">
                <q-btn
                  label="Yes"
                  outline
                  no-caps
                  color="secondary"
                  @click="clearOperatorAndNetworkInputs()"
                  v-close-popup
                />
                <q-btn
                  unelevated
                  no-caps
                  label="No"
                  color="secondary"
                  v-close-popup
                  @click="form.seeAllOperatorsAndNetworks = false"
                />
              </q-card-actions>
            </q-card>
          </q-dialog>
        </div>

        <div class="separator full-width q-py-md"><span>Or</span></div>

        <p class="q-mt-none">
          Provide user with access by Operator or Network, multiple values can
          be given. Please note, giving a user access to an Operator will also
          provide them with all Networks associated with that Operator.
        </p>

        <!-- Operators -->
        <q-select
          v-model="form.operators"
          ref="operators"
          :disable="this.form.seeAllOperatorsAndNetworks"
          :readonly="this.form.seeAllOperatorsAndNetworks"
          :options="operatorList"
          :clearable="form.operators.length !== 0 ? true : false"
          clear-icon="close"
          @clear="form.operators = []"
          :behavior="$q.platform.is.ios === true ? 'dialog' : 'menu'"
          option-value="OperatorID"
          option-label="Operator"
          emit-value
          map-options
          outlined
          color="secondary"
          multiple
          :use-chips="form.operators.length !== 0 ? true : false"
          class="select"
          popup-content-class="select__options"
          options-dense
          label="Operators *"
          :rules="[(val) => validOperator(val)]"
          ><template v-slot:option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
              class="select__options--item"
            >
              <q-item-section>
                <q-item-label>
                  {{ scope.opt.Operator }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
          <template v-slot:prepend>
            <q-icon name="corporate_fare" />
          </template>
        </q-select>

        <!-- Network -->
        <q-select
          v-model="form.networks"
          ref="networks"
          :disable="this.form.seeAllOperatorsAndNetworks"
          :options="networkList"
          :clearable="form.networks.length !== 0 ? true : false"
          clear-icon="close"
          @clear="form.networks = []"
          :behavior="$q.platform.is.ios === true ? 'dialog' : 'menu'"
          option-value="NetworkID"
          option-label="Network"
          emit-value
          map-options
          outlined
          color="secondary"
          multiple
          :use-chips="form.networks.length !== 0 ? true : false"
          class="select"
          popup-content-class="select__options"
          options-dense
          label="Networks *"
          lazy-rules
          :rules="[(val) => validNetwork(val)]"
          ><template v-slot:option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
              class="select__options--item"
            >
              <q-item-section>
                <q-item-label>
                  {{ scope.opt.Network }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
          <template v-slot:prepend>
            <q-icon name="podcasts" />
          </template>
        </q-select>
        <div>
          <q-btn
            label="Create User"
            type="submit"
            color="secondary"
            class="full-width"
            unelevated
            no-caps
          />
          <p v-if="error" class="text-red q-mt-md">{{ error }}</p>
        </div>
      </q-form>
    </div>
    <SuccessMessage :message="this.success" v-show="success" />
  </div>
</template>
<script>
import SuccessMessage from "@/components/other/SuccessMessage.vue";
export default {
  props: {
    formType: String,
  },
  data() {
    return {
      form: {
        email: "",
        firstname: "",
        surname: "",
        operators: [],
        networks: [],
        userRoles: [],
        pbiRoles: [],
        seeAllOperatorsAndNetworks: false,
      },
      operatorList: null,
      networkList: null,
      roleList: null,
      pbiRoleList: null,
      filterOption: {
        operator: null,
        network: null,
      },
      success: false,
      error: null,
      confirm: false,
    };
  },
  components: {
    SuccessMessage,
  },

  mounted() {
    // Fetch all filter lists on load. Use persistent operator and networks if set.
    this.fetchOperator();
    this.fetchNetwork();
    this.fetchUserRoles();
    this.fetchPbiRoles();
  },

  methods: {
    // Fetch all data
    fetchOperator() {
      // No dataset or filter options for operator list, store will handle operator ID.
      const payload = {
        filterOptions: {},
        report: "operator-list",
        dataset: "",
      };

      this.$store
        .dispatch("fetchData", payload)
        .then((response) => {
          this.operatorList = response.data;
        })
        .catch((error) => {
          this.error.push(error);
        });
    },
    fetchNetwork() {
      // No dataset for network list, store will handle operator ID if not supplied.
      const payload = {
        filterOptions: {},
        report: "network-list",
        dataset: "",
      };

      this.$store
        .dispatch("fetchData", payload)
        .then((response) => {
          if (response.data.length == 0) {
            this.networkList = [{ Network: "None", NetworkID: 0 }];
          } else {
            this.networkList = response.data;
          }
        })
        .catch((error) => {
          this.error.push(error);
        });
    },
    fetchUserRoles() {
      // No dataset or filter options for operator list, store will handle operator ID.
      const payload = {
        filterOptions: {},
        report: "role-list",
        dataset: "Role_List",
      };

      this.$store
        .dispatch("fetchData", payload)
        .then((response) => {
          this.roleList = response.data;
        })
        .catch((error) => {
          this.error.push(error);
        });
    },
    fetchPbiRoles() {
      // No dataset or filter options for operator list, store will handle operator ID.
      const payload = {
        filterOptions: { operatorId: this.$store.state.userDetails.operatorId },
        report: "pbi-role-list",
        dataset: "PBI_Role_List",
      };

      this.$store
        .dispatch("fetchData", payload)
        .then((response) => {
          this.pbiRoleList = response.data;
        })
        .catch((error) => {
          this.error.push(error);
        });
    },
    isClientAdminUser() {
      if (this.roleList) {
        return this.roleList.length == 2;
      } else {
        return false;
      }
    },
    // Input validations
    isValidEmail(val) {
      const emailPattern =
        /^(?=[a-zA-Z0-9@._%+-]{6,254}$)[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/;
      return emailPattern.test(val) || "Invalid email";
    },
    // Validate operator input
    validOperator(val) {
      if ((val && val.length > 0) || this.form.networks.length > 0) {
        this.$refs.networks.resetValidation();
        return true;
      } else if (this.form.seeAllOperatorsAndNetworks === true) {
        return true;
      } else {
        return "* Please add a Operator or Network";
      }
    },
    // Validate network input
    validNetwork(val) {
      if ((val && val.length > 0) || this.form.operators.length > 0) {
        this.$refs.operators.resetValidation();
        return true;
      } else if (this.form.seeAllOperatorsAndNetworks === true) {
        return true;
      } else {
        return "* Please add a Network or Operator";
      }
    },
    // Confirm user permissions dialog
    alert(confirm) {
      if (confirm) {
        this.confirm = true;
      } else {
        this.confirm = false;
      }
    },
    // Clear Operator and Network input when the show all checkbox is ticked
    clearOperatorAndNetworkInputs() {
      this.$refs.operators.resetValidation();
      this.form.operators = [];
      this.$refs.networks.resetValidation();
      this.form.networks = [];
    },
    // Create user
    onSubmitCreateUser: function (event) {
      // create payload to pass to create user action. Operator and Network default to 0 if no selection made.
      const payload = {
        email: this.form.email,
        firstname: this.form.firstname,
        surname: this.form.surname,
        operators: this.form.operators.length >= 1 ? this.form.operators : [],
        networks: this.form.networks.length >= 1 ? this.form.networks : [],
        roles: this.form.userRoles,
        pbiRoles: this.form.pbiRoles,
        seeAllOperatorsAndNetworks: this.form.seeAllOperatorsAndNetworks,
      };

      console.log("payload to submiut....", event, payload);
      // After payload constructed we hit the createUser action in the store
      this.$store
        .dispatch("createUser", payload)
        .then((data) => {
          // If 200 OK we can create the user else we send error back to the front end - such as user already exists.
          if (data.status === 200) {
            this.success =
              "User created - a confirmation email has been sent to them";
            // After confirmation of the user being created, we then reset the form.
            this.form.email = "";
            this.form.firstname = "";
            this.form.surname = "";
            this.form.userRoles = [];
            this.form.operators = [];
            this.form.networks = [];
            this.form.pbiRoles = [];
            this.form.seeAllOperatorsAndNetworks = false;
            event.target.reset();
            setTimeout(() => {
              this.success = "";
              this.error = "";
            }, 4000);
          } else if (data.response.status === 400) {
            this.error = data.response.data.message;
          }
        })
        .catch((error) => {
          this.error = error;
        });
    },
  },
};
</script>

<style lang="scss">
.form-container {
  display: flex;
  width: 100%;
  height: 100%;
}
.separator {
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  width: 100%;
  font-weight: 400;
  margin-top: 0;
  &:before,
  &:after {
    height: 1px;
    width: 45%;
    background: #c5c5c5;
    content: "";
  }
  span {
    max-width: 250px;
    margin: auto 1em;
  }
}
</style>
